import {assert} from 'Asserts';
import {ProcedureWithMetadata} from 'procedures/ProcedureDomainTypes';
import {
  legacyProcedureDirtyStateChanged,
  legacyProcedureIsSaveInFlightStateChanged,
  ProceduresAction,
} from 'procedures/redux/proceduresActions';
import {SingleProcedureReduxData} from 'procedures/redux/reduceProcedures';
import {getStore} from 'redux-stores/Store';

function getAllReduxData(itemId: string): SingleProcedureReduxData | undefined {
  return getStore().getState().models.procedures[itemId];
}

function isDirty(itemId: string): boolean {
  return Boolean(getAllReduxData(itemId)?.liveProperties.isDirty);
}

function isReadOnly(itemId: string): boolean {
  return Boolean(getAllReduxData(itemId)?.liveProperties.isReadOnly);
}

/** @package */
export function shouldSaveNow(itemId: string): boolean {
  const modelData = getAllReduxData(itemId);
  if (modelData != null) {
    const {liveProperties} = modelData;

    return (
      liveProperties.isDirty &&
      !liveProperties.isReadOnly &&
      !liveProperties.isAutosaverPaused &&
      !liveProperties.isAutosaverBlocked
    );
  } else {
    return false;
  }
}

/** @package */
export function getDataToSave(itemId: string): ProcedureWithMetadata {
  const data = getAllReduxData(itemId);
  assert(data != null, 'Autosaver is not initialized.', {itemId});
  assert(data.procedureMetadata != null, 'Procedure metadata is not initialized.', {itemId});
  return {
    flowchart: data.flowchart,
    metadata: data.procedureMetadata,
    processFlowchartConfigId: null,
  };
}

function emitAction(action: ProceduresAction): void {
  getStore().dispatch(action);
}

function setIsSaving(itemId: string, isSaveInFlight: boolean): void {
  emitAction(legacyProcedureIsSaveInFlightStateChanged(itemId, isSaveInFlight));
}

// Not a callback for AutosaveManager, but useful separately
export function setIsDirty(itemId: string, isDirty: boolean): void {
  emitAction(legacyProcedureDirtyStateChanged(itemId, isDirty));
}

// Ensure that an object is added to the store
/** @package */
export function seed(itemId: string): void {
  setIsDirty(itemId, false);
}

export default {
  seed,
  isDirty,
  isReadOnly,
  shouldSaveNow,
  getDataToSave,
  setIsSaving,
};
